
import { debugLogger } from '@/utils/debugLogger';

export interface PlaybackState {
  isPlaying: boolean;
  audio: HTMLAudioElement;
  duration: number;
  isCleaningUp?: boolean; // Add flag to track cleanup state
}

interface AudioEngineCallbacks {
  userId: string;
  onPlaybackStart?: (audioId: string) => void;
  onPlaybackEnd?: (audioId: string) => void;
  onPlaybackError?: (audioId: string, error: Error) => void;
}

class AudioEngine {
  private static instance: AudioEngine;
  private playbackStates = new Map<string, PlaybackState>();
  private callbacks: AudioEngineCallbacks | null = null;
  private currentUserId: string | null = null;

  private constructor() {
    debugLogger.debug('AudioEngine', 'AudioEngine instance created');
  }

  static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  initialize(callbacks: AudioEngineCallbacks): void {
    this.callbacks = callbacks;
    this.currentUserId = callbacks.userId;
    debugLogger.debug('AudioEngine', 'Initialized for user:', callbacks.userId);
  }

  async startPlayback(audioId: string, audioUrl: string): Promise<boolean> {
    try {
      debugLogger.debug('AudioEngine', 'Starting playback:', audioId);

      // Validate URL before creating audio element
      if (!audioUrl || audioUrl.trim().length === 0) {
        throw new Error('Invalid audio URL provided');
      }

      // Stop any existing playback for this audio
      this.stopPlayback(audioId);

      // Stop all other audio playback to ensure only one plays at a time
      this.stopAllPlayback();

      const audio = new Audio();
      
      // Set up error handling before loading
      const setupAudioElement = (): Promise<void> => {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            cleanup();
            reject(new Error('Audio loading timeout'));
          }, 10000); // 10 second timeout

          const cleanup = () => {
            clearTimeout(timeout);
            audio.removeEventListener('loadedmetadata', onLoad);
            audio.removeEventListener('error', onError);
            audio.removeEventListener('canplaythrough', onCanPlay);
          };

          const onLoad = () => {
            debugLogger.debug('AudioEngine', 'Audio metadata loaded:', {
              audioId,
              duration: audio.duration,
              readyState: audio.readyState
            });
            
            if (audio.duration > 0 && isFinite(audio.duration)) {
              cleanup();
              resolve();
            }
          };

          const onCanPlay = () => {
            debugLogger.debug('AudioEngine', 'Audio can play through:', audioId);
            cleanup();
            resolve();
          };

          const onError = (event: any) => {
            cleanup();
            const errorMsg = event?.target?.error?.message || 'Unknown audio error';
            debugLogger.error('AudioEngine', 'Audio loading error:', {
              audioId,
              error: errorMsg,
              code: event?.target?.error?.code
            });
            reject(new Error(`Audio loading failed: ${errorMsg}`));
          };

          audio.addEventListener('loadedmetadata', onLoad);
          audio.addEventListener('canplaythrough', onCanPlay);
          audio.addEventListener('error', onError);
          
          // Configure audio element
          audio.crossOrigin = 'anonymous';
          audio.preload = 'metadata';
          audio.src = audioUrl;
          audio.load();
        });
      };

      // Load the audio
      await setupAudioElement();

      // Create playback state first
      const playbackState: PlaybackState = {
        isPlaying: false,
        audio,
        duration: audio.duration || 0,
        isCleaningUp: false
      };

      this.playbackStates.set(audioId, playbackState);

      // Set up playback event listeners with cleanup protection
      const onEnded = () => {
        const state = this.playbackStates.get(audioId);
        if (state && !state.isCleaningUp) {
          debugLogger.debug('AudioEngine', 'Playback ended naturally:', audioId);
          this.stopPlayback(audioId);
          this.callbacks?.onPlaybackEnd?.(audioId);
        }
      };

      const onPlaybackError = (event: any) => {
        const state = this.playbackStates.get(audioId);
        if (state && !state.isCleaningUp) {
          const errorMsg = event?.target?.error?.message || 'Playback error';
          debugLogger.error('AudioEngine', 'Playback error during playback:', {
            audioId,
            error: errorMsg,
            isCleaningUp: state.isCleaningUp
          });
          this.stopPlayback(audioId);
          this.callbacks?.onPlaybackError?.(audioId, new Error(errorMsg));
        } else {
          debugLogger.debug('AudioEngine', 'Ignoring error during cleanup:', audioId);
        }
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onPlaybackError);

      // Store event listeners for proper cleanup
      (audio as any)._audioEngineListeners = {
        ended: onEnded,
        error: onPlaybackError
      };

      // Start playback
      await audio.play();
      playbackState.isPlaying = true;

      debugLogger.debug('AudioEngine', 'Playback started successfully:', {
        audioId,
        duration: audio.duration
      });

      this.callbacks?.onPlaybackStart?.(audioId);
      return true;

    } catch (error) {
      debugLogger.error('AudioEngine', 'Failed to start playback:', {
        audioId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      this.callbacks?.onPlaybackError?.(audioId, error instanceof Error ? error : new Error('Unknown playback error'));
      return false;
    }
  }

  pausePlayback(audioId: string): void {
    const state = this.playbackStates.get(audioId);
    if (state && state.isPlaying && !state.isCleaningUp) {
      state.audio.pause();
      state.isPlaying = false;
      debugLogger.debug('AudioEngine', 'Playback paused:', audioId);
    }
  }

  resumePlayback(audioId: string): void {
    const state = this.playbackStates.get(audioId);
    if (state && !state.isPlaying && !state.isCleaningUp) {
      state.audio.play().then(() => {
        const currentState = this.playbackStates.get(audioId);
        if (currentState && !currentState.isCleaningUp) {
          currentState.isPlaying = true;
          debugLogger.debug('AudioEngine', 'Playback resumed:', audioId);
        }
      }).catch((error) => {
        const currentState = this.playbackStates.get(audioId);
        if (currentState && !currentState.isCleaningUp) {
          debugLogger.error('AudioEngine', 'Failed to resume playback:', error);
          this.callbacks?.onPlaybackError?.(audioId, error);
        }
      });
    }
  }

  stopPlayback(audioId: string): void {
    const state = this.playbackStates.get(audioId);
    if (state) {
      debugLogger.debug('AudioEngine', 'Starting cleanup for:', audioId);
      
      // Mark as cleaning up to prevent race conditions
      state.isCleaningUp = true;
      
      // Remove event listeners FIRST to prevent spurious events
      const listeners = (state.audio as any)._audioEngineListeners;
      if (listeners) {
        state.audio.removeEventListener('ended', listeners.ended);
        state.audio.removeEventListener('error', listeners.error);
        delete (state.audio as any)._audioEngineListeners;
      }
      
      // Stop playback
      if (!state.audio.paused) {
        state.audio.pause();
      }
      
      // Reset position
      state.audio.currentTime = 0;
      state.isPlaying = false;
      
      // Clean up the audio element safely
      try {
        state.audio.src = '';
        state.audio.load();
      } catch (error) {
        // Ignore cleanup errors - they're not critical
        debugLogger.debug('AudioEngine', 'Cleanup error (ignored):', error);
      }
      
      // Remove from state map
      this.playbackStates.delete(audioId);
      
      debugLogger.debug('AudioEngine', 'Playback stopped and cleaned up:', audioId);
    }
  }

  stopAllPlayback(): void {
    const activeAudios = Array.from(this.playbackStates.keys());
    activeAudios.forEach(audioId => {
      this.stopPlayback(audioId);
    });
    
    if (activeAudios.length > 0) {
      debugLogger.debug('AudioEngine', 'Stopped all playback:', activeAudios.length, 'audios');
    }
  }

  seekTo(audioId: string, time: number): void {
    const state = this.playbackStates.get(audioId);
    if (state && !state.isCleaningUp) {
      state.audio.currentTime = Math.max(0, Math.min(time, state.duration));
      debugLogger.debug('AudioEngine', 'Seeked to:', { audioId, time });
    }
  }

  getPlaybackState(audioId: string): PlaybackState | null {
    const state = this.playbackStates.get(audioId);
    return (state && !state.isCleaningUp) ? state : null;
  }

  // Clean up all resources
  destroy(): void {
    this.stopAllPlayback();
    this.playbackStates.clear();
    this.callbacks = null;
    this.currentUserId = null;
    debugLogger.debug('AudioEngine', 'AudioEngine destroyed');
  }
}

export const audioEngine = AudioEngine.getInstance();

// Cleanup on page unload (disabled during tests)
if (!(typeof import.meta !== 'undefined' && (import.meta as any).env?.VITEST)) {
  window.addEventListener('beforeunload', () => {
    audioEngine.destroy();
  });
}
